import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApp } from "../context/AppContext";
import {
  assignments as assignmentsApi, users as usersApi, stores as storesApi, templates as templatesApi,
} from "../lib/api";
import { localizedTemplate } from "../lib/i18nContent";
import { STATUS_META, PRIORITY_META, fmtDate, mondayOf } from "../lib/assignmentMeta";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import CreateAssignmentDialog from "../components/CreateAssignmentDialog";
import { toast } from "sonner";
import {
  ClipboardCheck, Plus, CalendarClock, Store as StoreIcon, ListTodo, Play,
  ArrowRight, Clock, ChevronLeft, ChevronRight, CalendarDays, ListChecks, Filter,
} from "lucide-react";

function StatusPill({ status }) {
  const { t } = useTranslation();
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`} data-testid={`status-pill-${status}`}>
      {t(m.key)}
    </span>
  );
}

function PriorityPill({ priority }) {
  const { t } = useTranslation();
  const m = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${m.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} /> {t(m.key)}
    </span>
  );
}

function AssignmentCard({ a, showAuditor, onOpen, onStart, starting }) {
  const { t, i18n } = useTranslation();
  const isTask = a.type === "task_checklist";
  const st = a.effective_status;
  const canStart = st === "pending" || st === "overdue";
  const canContinue = st === "in_progress";
  const heading = isTask ? (a.title || t("assignments.type_task_checklist")) : a.store_name;
  return (
    <Card
      className="canales-card p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onOpen(a)}
      data-testid={`assignment-card-${a.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${isTask ? "bg-indigo-600" : "bg-[#1E3A8A]"} text-white`}>
            {isTask ? <ListTodo size={16} /> : <StoreIcon size={16} />}
          </span>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate">{heading}</div>
            <div className="text-[11px] text-stone-500 truncate">
              {isTask
                ? t("assignments.type_task_checklist")
                : `${a.ciudad || ""}${a.distrito ? ` · ${a.distrito}` : ""}`}
            </div>
          </div>
        </div>
        <StatusPill status={st} />
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3">
        {isTask ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full">
            <ListChecks size={11} /> {(a.checklist_items || []).length}
          </span>
        ) : (
          (a.categories || []).map((c) => (
            <span key={c} className="inline-flex items-center text-[11px] text-[#1E3A8A] bg-[#E5EBFA] px-2 py-0.5 rounded-full font-medium">
              {localizedTemplate(c, i18n.language)}
            </span>
          ))
        )}
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100">
        <div className="flex items-center gap-3 text-[11px] text-stone-500">
          <span className="inline-flex items-center gap-1">
            <CalendarClock size={12} /> {a.due_date ? fmtDate(a.due_date, i18n.language) : t("assignments.no_due")}
          </span>
          <PriorityPill priority={a.priority} />
        </div>
        {showAuditor && <span className="text-[11px] text-stone-500 truncate max-w-[100px]">{a.auditor_name}</span>}
      </div>

      <div className="mt-3">
        {canStart && (
          <Button
            size="sm"
            className="brand-button w-full gap-1.5"
            disabled={starting}
            onClick={(e) => { e.stopPropagation(); onStart(a); }}
            data-testid={`start-task-${a.id}`}
          >
            <Play size={13} /> {t("assignments.start_task")}
          </Button>
        )}
        {canContinue && (
          <Button
            size="sm"
            variant="outline"
            className="w-full gap-1.5"
            onClick={(e) => { e.stopPropagation(); onOpen(a); }}
            data-testid={`continue-task-${a.id}`}
          >
            {isTask ? <ListChecks size={13} /> : <ClipboardCheck size={13} />}
            {isTask ? t("assignments.view_detail") : t("assignments.continue_audit")}
          </Button>
        )}
        {!canStart && !canContinue && (
          <Button
            size="sm"
            variant="ghost"
            className="w-full gap-1.5 text-stone-600"
            onClick={(e) => { e.stopPropagation(); onOpen(a); }}
            data-testid={`view-task-${a.id}`}
          >
            {t("assignments.view_detail")} <ArrowRight size={13} />
          </Button>
        )}
      </div>
    </Card>
  );
}

export default function AssignmentsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can, setActiveAuditId, setActiveStore } = useApp();
  const isManager = can("manage_assignments");

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [tab, setTab] = useState("list");

  const [auditors, setAuditors] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [filters, setFilters] = useState({ auditor_id: "", store_id: "", status: "", category: "", type: "", q: "" });

  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [schedule, setSchedule] = useState({ groups: [], total: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const data = await assignmentsApi.list(params);
      setList(data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const id = setTimeout(load, filters.q ? 300 : 0);
    return () => clearTimeout(id);
  }, [load, filters.q]);

  useEffect(() => {
    if (!isManager) return;
    Promise.all([usersApi.assignable(), storesApi.list(true), templatesApi.list()])
      .then(([u, s, tpl]) => {
        setAuditors((u || []).filter((x) => x.role === "auditor" || x.role === "gerente"));
        setStoreList(s || []);
        setTemplateList(tpl || []);
      })
      .catch(() => {});
  }, [isManager]);

  const loadSchedule = useCallback(async () => {
    try {
      const data = await assignmentsApi.schedule({ week_start: weekStart.toISOString().slice(0, 10) });
      setSchedule(data || { groups: [], total: 0 });
    } catch {
      setSchedule({ groups: [], total: 0 });
    }
  }, [weekStart]);

  useEffect(() => {
    if (isManager && tab === "schedule") loadSchedule();
  }, [isManager, tab, loadSchedule]);

  const handleStart = async (a) => {
    setStarting(a.id);
    try {
      const res = await assignmentsApi.start(a.id);
      toast.success(t("assignments.toast_started"));
      if (a.type === "store_audit" && res.audit_id) {
        setActiveAuditId(res.audit_id);
        setActiveStore({ id: a.store_id, tienda: a.store_name, ciudad: a.ciudad });
        navigate(`/audit/${res.audit_id}`);
      } else {
        navigate(`/assignments/${a.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("assignments.toast_error"));
    } finally {
      setStarting(null);
    }
  };

  const openDetail = (a) => navigate(`/assignments/${a.id}`);

  const grouped = useMemo(() => {
    const g = { pending: [], overdue: [], in_progress: [], completed: [], cancelled: [] };
    for (const a of list) (g[a.effective_status] || g.pending).push(a);
    return g;
  }, [list]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v === "__all__" ? "" : v }));
  const clearFilters = () => setFilters({ auditor_id: "", store_id: "", status: "", category: "", type: "", q: "" });

  // ---- Weekly schedule drag & drop ----
  const [dragId, setDragId] = useState(null);
  const onDropTo = async (auditorId) => {
    const id = dragId;
    setDragId(null);
    if (!id || !auditorId) return;
    const item = schedule.groups.flatMap((g) => g.items).find((x) => x.id === id);
    if (!item || item.auditor_id === auditorId) return;
    try {
      await assignmentsApi.reassign(id, auditorId, "");
      toast.success(t("assignments.toast_reassigned"));
      loadSchedule();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("assignments.toast_error"));
    }
  };

  const header = (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#1E3A8A] grid place-items-center text-white">
          <ClipboardCheck size={18} />
        </span>
        <div>
          <div className="canales-overline">{t("assignments.overline")}</div>
          <h1 className="font-display text-3xl md:text-4xl mt-1">
            {isManager ? t("assignments.title") : t("assignments.my_tasks_title")}
          </h1>
          <p className="text-stone-600 mt-1 text-sm">
            {isManager ? t("assignments.subtitle") : t("assignments.my_tasks_subtitle")}
          </p>
        </div>
      </div>
      {isManager && (
        <Button className="brand-button gap-2" onClick={() => setCreateOpen(true)} data-testid="new-assignment-button">
          <Plus size={16} /> {t("assignments.new_assignment")}
        </Button>
      )}
    </div>
  );

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="assignments-page">
      {header}

      {isManager ? (
        <>
          {/* Tabs */}
          <div className="flex items-center gap-1 mt-6 bg-stone-100 p-1 rounded-full w-fit">
            <button
              onClick={() => setTab("list")}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${tab === "list" ? "bg-white shadow-sm text-[#1E3A8A]" : "text-stone-500"}`}
              data-testid="assignments-tab-list"
            >
              <ListTodo size={14} /> {t("assignments.tab_list")}
            </button>
            <button
              onClick={() => setTab("schedule")}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${tab === "schedule" ? "bg-white shadow-sm text-[#1E3A8A]" : "text-stone-500"}`}
              data-testid="assignments-tab-schedule"
            >
              <CalendarDays size={14} /> {t("assignments.tab_schedule")}
            </button>
          </div>

          {tab === "list" && (
            <>
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2 mt-5">
                <div className="relative">
                  <Filter size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    value={filters.q}
                    onChange={(e) => setF("q", e.target.value)}
                    placeholder={t("assignments.search_ph")}
                    className="pl-8 w-64 h-9"
                    data-testid="assignments-search"
                  />
                </div>
                <Select value={filters.auditor_id || "__all__"} onValueChange={(v) => setF("auditor_id", v)}>
                  <SelectTrigger className="w-40 h-9" data-testid="filter-auditor"><SelectValue placeholder={t("assignments.filter_auditor")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">{t("assignments.all")} · {t("assignments.filter_auditor")}</SelectItem>
                    {auditors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.status || "__all__"} onValueChange={(v) => setF("status", v)}>
                  <SelectTrigger className="w-36 h-9" data-testid="filter-status"><SelectValue placeholder={t("assignments.filter_status")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">{t("assignments.all")} · {t("assignments.filter_status")}</SelectItem>
                    {["pending", "in_progress", "completed", "overdue", "cancelled"].map((s) => (
                      <SelectItem key={s} value={s}>{t(STATUS_META[s].key)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.type || "__all__"} onValueChange={(v) => setF("type", v)}>
                  <SelectTrigger className="w-40 h-9" data-testid="filter-type"><SelectValue placeholder={t("assignments.filter_type")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">{t("assignments.all")} · {t("assignments.filter_type")}</SelectItem>
                    <SelectItem value="store_audit">{t("assignments.type_store_audit")}</SelectItem>
                    <SelectItem value="task_checklist">{t("assignments.type_task_checklist")}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.category || "__all__"} onValueChange={(v) => setF("category", v)}>
                  <SelectTrigger className="w-36 h-9" data-testid="filter-category"><SelectValue placeholder={t("assignments.filter_category")} /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__all__">{t("assignments.all")} · {t("assignments.filter_category")}</SelectItem>
                    {templateList.map((tpl) => <SelectItem key={tpl.id} value={tpl.name}>{localizedTemplate(tpl.name, i18n.language)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={clearFilters} className="text-xs text-[#1E3A8A] font-medium hover:underline" data-testid="clear-filters">
                  {t("assignments.clear_filters")}
                </button>
              </div>

              {/* List grid */}
              {loading ? (
                <div className="p-16 text-center text-stone-400">{t("common.loading")}</div>
              ) : list.length === 0 ? (
                <EmptyState manager onCreate={() => setCreateOpen(true)} />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5" data-testid="assignments-list">
                  {list.map((a) => (
                    <AssignmentCard key={a.id} a={a} showAuditor onOpen={openDetail} onStart={handleStart} starting={starting === a.id} />
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "schedule" && (
            <div className="mt-5" data-testid="assignments-schedule">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setWeekStart(mondayOf(new Date(weekStart.getTime() - 7 * 864e5)))} data-testid="week-prev">
                    <ChevronLeft size={14} /> {t("assignments.prev_week")}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="week-this">
                    {t("assignments.this_week")}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setWeekStart(mondayOf(new Date(weekStart.getTime() + 7 * 864e5)))} data-testid="week-next">
                    {t("assignments.next_week")} <ChevronRight size={14} />
                  </Button>
                </div>
                <span className="text-sm font-semibold text-stone-700">
                  {t("assignments.week")}: {fmtDate(weekStart.toISOString().slice(0, 10), i18n.language)}
                </span>
              </div>
              <p className="text-[11px] text-stone-400 mt-2">{t("assignments.drag_hint")}</p>

              <div className="mt-4 space-y-4">
                {(auditors.length ? auditors : schedule.groups.map((g) => ({ id: g.auditor_id, name: g.auditor_name }))).map((aud) => {
                  const group = schedule.groups.find((g) => g.auditor_id === aud.id);
                  const items = group?.items || [];
                  const initials = (aud.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
                  if (items.length === 0) {
                    return (
                      <div
                        key={aud.id || "unassigned"}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => onDropTo(aud.id)}
                        className="flex items-center gap-2 rounded-lg border border-dashed border-stone-200 px-3 py-2"
                        data-testid={`schedule-auditor-${aud.id}`}
                      >
                        <span className="w-6 h-6 rounded-full bg-stone-100 text-stone-500 grid place-items-center text-[10px] font-bold">{initials}</span>
                        <span className="text-sm font-medium text-stone-600">{aud.name}</span>
                        <span className="text-[11px] text-stone-400 ml-auto">{t("assignments.no_schedule")}</span>
                      </div>
                    );
                  }
                  return (
                    <Card
                      key={aud.id || "unassigned"}
                      className="canales-card p-4"
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={() => onDropTo(aud.id)}
                      data-testid={`schedule-auditor-${aud.id}`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-7 h-7 rounded-full bg-[#1E3A8A] text-[#FACC15] grid place-items-center text-[11px] font-bold">
                          {initials}
                        </span>
                        <span className="font-semibold text-sm">{aud.name}</span>
                        <span className="text-[11px] text-stone-400">· {items.length}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {items.map((it) => (
                          <div
                            key={it.id}
                            draggable
                            onDragStart={() => setDragId(it.id)}
                            onClick={() => openDetail(it)}
                            className="cursor-grab active:cursor-grabbing rounded-lg border border-stone-200 bg-white px-3 py-2 hover:border-[#1E3A8A] transition-colors w-full sm:w-auto sm:max-w-[280px]"
                            data-testid={`schedule-item-${it.id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold truncate">
                                {it.type === "task_checklist" ? (it.title || t("assignments.type_task_checklist")) : it.store_name}
                              </span>
                              <StatusPill status={it.effective_status} />
                            </div>
                            <div className="text-[11px] text-stone-500 mt-1 truncate">
                              {it.type === "task_checklist"
                                ? t("assignments.type_task_checklist")
                                : (it.categories || []).map((c) => localizedTemplate(c, i18n.language)).join(", ")}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : (
        /* ---- Auditor board ---- */
        <div className="mt-6">
          {loading ? (
            <div className="p-16 text-center text-stone-400">{t("common.loading")}</div>
          ) : list.length === 0 ? (
            <EmptyState onCreate={null} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-5" data-testid="auditor-board">
              {[
                { key: "pending", items: grouped.pending, icon: Clock },
                { key: "in_progress", items: grouped.in_progress, icon: Play },
                { key: "overdue", items: grouped.overdue, icon: CalendarClock },
                { key: "completed", items: grouped.completed, icon: ClipboardCheck },
              ].map((col) => (
                <div key={col.key} data-testid={`board-col-${col.key}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <col.icon size={15} className="text-stone-500" />
                    <span className="text-sm font-semibold">{t(STATUS_META[col.key].key)}</span>
                    <span className="text-[11px] text-stone-400">· {col.items.length}</span>
                  </div>
                  <div className="space-y-3">
                    {col.items.map((a) => (
                      <AssignmentCard key={a.id} a={a} onOpen={openDetail} onStart={handleStart} starting={starting === a.id} />
                    ))}
                    {col.items.length === 0 && (
                      <div className="text-xs text-stone-300 border border-dashed border-stone-200 rounded-lg py-6 text-center">—</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <CreateAssignmentDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={() => load()} />
    </div>
  );
}

function EmptyState({ manager, onCreate }) {
  const { t } = useTranslation();
  return (
    <div className="mt-10 border border-dashed border-stone-200 rounded-2xl p-14 text-center" data-testid="assignments-empty">
      <span className="w-14 h-14 rounded-2xl bg-stone-100 grid place-items-center mx-auto text-stone-400">
        <ClipboardCheck size={26} />
      </span>
      <h3 className="font-display text-xl mt-4">{manager ? t("assignments.empty_title") : t("assignments.empty_auditor")}</h3>
      {manager && <p className="text-sm text-stone-500 mt-1">{t("assignments.empty_hint")}</p>}
      {manager && onCreate && (
        <Button className="brand-button gap-2 mt-5" onClick={onCreate}>
          <Plus size={15} /> {t("assignments.new_assignment")}
        </Button>
      )}
    </div>
  );
}
